import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { AxiosResponse } from 'axios';

import {
  IGetUsersResponse,
  IGetUserByIdResponse,
  IUpdateUserRequest,
  IUpdateUserResponse,
  ICreateUserInvitationRequest,
  ICreateUserResponse,
  IDeleteUserResponse,
  IDeleteUserFilters,
  IDeleteInvitationResponse,
  IResendInvitationResponse,
  IGetPendingInvitationsCountResponse,
} from 'models/http/user';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import { IGetSimpleListFilters, IGetSimpleListResponse, ISearchFilter, ISearchWithPaginationFilter } from 'models/http/common';
import { IGetHistoryResponse } from 'models/http/history';

class UserHttp {
  private static route = '/moderators';
  public static invitationsRoute = '/users/invitations';

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`${UserHttp.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async getSimple(_?: IGetSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    return serverAxios
      .get(`${UserHttp.route}/simple`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async getPendingInvitationsCount(abortController?: AbortController): Promise<IGetPendingInvitationsCountResponse> {
    return serverAxios
      .get(`${UserHttp.invitationsRoute}/pending`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetPendingInvitationsCountResponse>) => {
        return response.data;
      });
  }

  public async get(filters: ISearchWithPaginationFilter): Promise<IGetUsersResponse> {
    const queryParams = prepareQPFromFilters(filters);

    return serverAxios
      .get(`${UserHttp.route}?${queryParams.toString()}`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetUsersResponse>) => response.data);
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetUserByIdResponse> {
    return serverAxios
      .get(`${UserHttp.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetUserByIdResponse>) => response.data);
  }

  public async create(user: ICreateUserInvitationRequest): Promise<ICreateUserResponse> {
    return serverAxios
      .post(`${UserHttp.invitationsRoute}`, user, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateUserResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, user: IUpdateUserRequest): Promise<IUpdateUserResponse> {
    return serverAxios
      .patch(`${UserHttp.route}/${id}`, user, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IUpdateUserResponse>) => response.data);
  }

  public async delete(ids: number[]): Promise<IDeleteUserResponse>;
  public async delete(filters: IDeleteUserFilters): Promise<IDeleteUserResponse>;
  public async delete(filters: number[] | IDeleteUserFilters): Promise<IDeleteUserResponse> {
    return serverAxios
      .delete(UserHttp.route, { data: prepareFiltersOfSelection(filters) })
      .then((response: AxiosResponse<IDeleteUserResponse>) => {
        return response.data;
      });
  }

  public async deleteInvitations(ids: number[]): Promise<IDeleteInvitationResponse>;
  public async deleteInvitations(filters: IDeleteUserFilters): Promise<IDeleteInvitationResponse>;
  public async deleteInvitations(filters: number[] | IDeleteUserFilters): Promise<IDeleteInvitationResponse> {
    return serverAxios
      .delete(UserHttp.invitationsRoute, { data: prepareFiltersOfSelection(filters) })
      .then((response: AxiosResponse<IDeleteInvitationResponse>) => {
        return response.data;
      });
  }

  public async resendInvitations(ids: number[]): Promise<IResendInvitationResponse>;
  public async resendInvitations(filters: ISearchFilter): Promise<IResendInvitationResponse>;
  public async resendInvitations(filters: number[] | ISearchFilter): Promise<IResendInvitationResponse> {
    const data = prepareFiltersOfSelection(filters);

    return serverAxios
      .post(`${UserHttp.invitationsRoute}/resend`, data, { timeout: HttpTimeoutPriority.Lowest })
      .then((response: AxiosResponse<IResendInvitationResponse>) => {
        return response.data;
      });
  }

  public async changePassword(id: number, password: string): Promise<void> {
    return serverAxios.patch(`${UserHttp.route}/${id}/password`, { password }).then(() => {
      return;
    });
  }
}

export default UserHttp;
